import React, { useState, useEffect } from 'react'
import { BOOKMAKER_LOGOS } from '../config/bookmakers'
import SubjectImage from '../components/SubjectImage'

// Storage key matches EV8's so hidden bets carry over if user switches between
// the legacy /ev page and EV (New) during the rebuild.
const STORAGE_KEY = 'hidden-ev-bets'

// ID format matches EV8.generateHideBetId — stable across odds/bookmaker changes,
// keyed only by sport+match+market+selection+time.
export const oppKey = (opp) =>
  `${opp.sport}_${opp.match}_${opp.market_type}_${opp.selection}`
    .replace(/[^a-zA-Z0-9_]/g, '_')

// A whole match hidden at once, from the track modal's "Also hide the rest of
// this match". Start time is in the key so the same two teams meeting again
// later are not caught by it.
export const matchKey = (opp) =>
  `match|${opp.match}|${opp.commence_time}`

const formatDateTime = (ts) =>
  new Date(ts + 'Z').toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  })

const parseSelection = (sel) => {
  if (!sel) return { name: '', dir: null, line: null }
  const m = sel.match(/^(.+?)\s+(over|under)\s+(.+)$/i)
  if (!m) return { name: sel, dir: null, line: null }
  return { name: m[1], dir: m[2].toLowerCase(), line: m[3] }
}

// Normalise bookmakers (EV stores strings, PROP_EV stores objects) → always {name}.
const normaliseBookmakers = (list) => {
  if (!Array.isArray(list)) return []
  return list.map(b => (typeof b === 'string' ? { name: b } : b))
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useHiddenBets() {
  const [hiddenBets, setHiddenBets] = useState({})
  const [showModal,  setShowModal]  = useState(false)

  // On mount: load stored hidden bets, drop any whose game started > 1h ago
  // (matches EV8's loadHidden cleanup window).
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([key, data]) => {
      if (!data?.commence_time) return
      const cutoff = new Date(new Date(data.commence_time + 'Z').getTime() + 60 * 60 * 1000)
      if (now < cutoff) cleaned[key] = data
    })
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  const hideOpp = (opp, { wholeMatch = false } = {}) => {
    if (wholeMatch) {
      const updated = {
        ...hiddenBets,
        [matchKey(opp)]: {
          scope:         'match',
          sport:         opp.sport,
          match:         opp.match,
          commence_time: opp.commence_time,
        },
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
      setHiddenBets(updated)
      return
    }
    const key = oppKey(opp)
    const updated = {
      ...hiddenBets,
      [key]: {
        sport:         opp.sport,
        match:         opp.match,
        selection:     opp.selection,
        market_type:   opp.market_type,
        commence_time: opp.commence_time,
        // Needed by SubjectImage to pick the right image. Without them a hidden
        // player prop falls through to the match's two crests, because
        // settlementFields() can still split home/away out of `match`.
        // Entries hidden before this shipped have neither and degrade to that.
        player_name:   opp.player_name ?? null,
        team_name:     opp.team_name ?? null,
        ev_percentage: opp.ev_percentage,
        original_odds: opp.original_odds,
        bookmakers:    opp.bookmakers || (opp.bookmaker ? [opp.bookmaker] : []),
      },
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpp = (key) => {
    const updated = { ...hiddenBets }
    delete updated[key]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAll = () => {
    localStorage.removeItem(STORAGE_KEY)
    setHiddenBets({})
    setShowModal(false)
  }

  const isHidden = (opp) => !!(hiddenBets[oppKey(opp)] || hiddenBets[matchKey(opp)])

  return { hiddenBets, hideOpp, unhideOpp, clearAll, isHidden, showModal, setShowModal }
}

// A hidden match has no selection, odds or bookmaker, so it gets its own row:
// the match where a bet's name would be, and "Whole match" in place of the line.
function HiddenMatchRow({ bet, onUnhide, mobile }) {
  return (
    <div className="phm-row">
      <div className="phm-row-inner">
        <div className="phm-ev-subject">
          <SubjectImage opp={bet} />
        </div>
        {mobile ? (
          <div className="bcm-card-name-block">
            <div className="bcm-player-name">{bet.match}</div>
            <div className="bcm-match-name">Whole match</div>
            <div className="bcm-match-time">{formatDateTime(bet.commence_time)}</div>
          </div>
        ) : (
          <div className="phm-row-info">
            <div className="phm-row-sel">
              <span className="phm-player">{bet.match}</span>
            </div>
            <div className="phm-sub">Whole match</div>
            <div className="phm-sub">{formatDateTime(bet.commence_time)}</div>
          </div>
        )}
        <div className="phm-row-actions">
          <button className="phm-unhide-btn" onClick={onUnhide} data-tooltip="Unhide">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
              <circle cx="12" cy="12" r="3"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal ────────────────────────────────────────────────────────────────────

export default function HiddenBetsModal({ isOpen, hiddenBets, onUnhide, onUnhideAll, onClose, mobile = false }) {
  if (!isOpen) return null

  const entries = Object.entries(hiddenBets)

  return (
    <>
      <div className="bc-overlay" onClick={onClose} />
      <div className="phm-modal">

        <div className="phm-header">
          <div className="phm-header-left">
            <span className="phm-title">Hidden Bets</span>
            {entries.length > 0 && <span className="phm-count">{entries.length}</span>}
          </div>
          <div className="phm-header-right">
            {entries.length > 0 && (
              <button className="bc-btn phm-unhide-all" onClick={onUnhideAll}>
                Unhide All
              </button>
            )}
            <button className="bc-panel-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="phm-body">
          {entries.length === 0 ? (
            <div className="phm-empty">No hidden bets</div>
          ) : (
            entries.map(([key, bet]) => {
              if (bet.scope === 'match') {
                return <HiddenMatchRow key={key} bet={bet} onUnhide={() => onUnhide(key)} mobile={mobile} />
              }
              const { name, dir, line } = parseSelection(bet.selection)
              const evClass = bet.ev_percentage >= 10 ? 'bc-ev-high' : bet.ev_percentage >= 5 ? 'bc-ev-mid' : 'bc-ev-low'
              const books = normaliseBookmakers(bet.bookmakers)
              return (
                <div key={key} className="phm-row">
                  <div className="phm-row-inner">

                    {mobile ? (
                      <>
                        <div className="phm-ev-subject">
                          <SubjectImage opp={bet} />
                        </div>

                        <div className="bcm-card-name-block">
                          <div className="bcm-player-name">{name || bet.match}</div>
                          {dir && (
                            <div className={`bc-sel-badge ${dir}`}>
                              <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                              <span className="bc-sel-badge-num">{line}</span>
                            </div>
                          )}
                          <div className="bcm-match-name">{bet.match}</div>
                          <div className="bcm-match-time">{formatDateTime(bet.commence_time)}</div>
                        </div>

                        <div className="phm-row-actions">
                          <div className="bc-book-cell phm-book-centered">
                            {books.map(b =>
                              BOOKMAKER_LOGOS[b.name]
                                ? <img key={b.name} src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-book-logo" title={b.name} />
                                : <span key={b.name} className="phm-book-name">{b.name}</span>
                            )}
                          </div>
                          <button className="phm-unhide-btn" onClick={() => onUnhide(key)} data-tooltip="Unhide">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="phm-ev-subject">
                          <SubjectImage opp={bet} />
                        </div>

                        <div className="phm-row-info">
                          <div className="phm-row-sel">
                            <span className="phm-player">{name || bet.match}</span>
                            {dir && (
                              <span className={`bc-sel-badge ${dir}`}>
                                <span className="bc-sel-badge-dir">{dir.charAt(0).toUpperCase() + dir.slice(1)}</span>
                                <span className="bc-sel-badge-num">{line}</span>
                              </span>
                            )}
                          </div>
                          <div className="phm-sub">{bet.match}</div>
                          <div className="phm-sub">{formatDateTime(bet.commence_time)}</div>
                        </div>

                        <div className="phm-row-actions">
                          <div className="bc-book-cell">
                            {books.map(b =>
                              BOOKMAKER_LOGOS[b.name]
                                ? <img key={b.name} src={BOOKMAKER_LOGOS[b.name]} alt={b.name} className="bc-book-logo" title={b.name} />
                                : <span key={b.name} className="phm-book-name">{b.name}</span>
                            )}
                          </div>
                          {typeof bet.ev_percentage === 'number' && (
                            <span className={`bc-ev-badge ${evClass}`}>
                              +{bet.ev_percentage.toFixed(2)}%
                            </span>
                          )}
                          <button className="phm-unhide-btn" onClick={() => onUnhide(key)} data-tooltip="Unhide">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                              <circle cx="12" cy="12" r="3"/>
                            </svg>
                          </button>
                        </div>
                      </>
                    )}

                  </div>
                </div>
              )
            })
          )}
        </div>

      </div>
    </>
  )
}
